'use client'

import { useCallback, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CreditCard,
  Phone,
  Scale,
  ShieldCheck,
  Globe,
} from 'lucide-react'
import { Logomark } from '@/components/brand'
import { MetricCounters } from './MetricCounter'
import { TrustedByBar } from './TrustedByBar'
import { cn } from '@/lib/utils'

interface FeaturePillar {
  id: string
  icon: React.ReactNode
  title: string
  bullets: string[]
  previewGradient: string
  previewLabel: string
}

const PILLARS: FeaturePillar[] = [
  {
    id: 'cash',
    icon: <CreditCard className='h-5 w-5' strokeWidth={1.5} />,
    title: 'Cash Application',
    bullets: [
      'AI-powered payment matching with 90%+ STP rate',
      'Fuzzy reference matching and remittance parsing',
      'One-click exception resolution',
    ],
    previewGradient: 'from-brand-cornflower/30 via-brand-purple/20 to-brand-navy/50',
    previewLabel: 'Match Explorer',
  },
  {
    id: 'collections',
    icon: <Phone className='h-5 w-5' strokeWidth={1.5} />,
    title: 'Collections',
    bullets: [
      'AI-prioritized collection worklist',
      'Automated dunning with contextual emails',
      'Promise-to-pay tracking and follow-ups',
    ],
    previewGradient: 'from-emerald-500/20 via-brand-cornflower/20 to-brand-navy/50',
    previewLabel: 'Collection Queue',
  },
  {
    id: 'deductions',
    icon: <Scale className='h-5 w-5' strokeWidth={1.5} />,
    title: 'Deductions',
    bullets: [
      'AI validity prediction with reasoning trace',
      'Evidence management and SLA tracking',
      'Automated resolution workflows',
    ],
    previewGradient: 'from-amber-500/20 via-brand-purple/20 to-brand-navy/50',
    previewLabel: 'Deduction Center',
  },
  {
    id: 'credit',
    icon: <ShieldCheck className='h-5 w-5' strokeWidth={1.5} />,
    title: 'Credit Intelligence',
    bullets: [
      'AI composite risk scoring with external signals',
      'Automated credit reviews and limit adjustments',
      'Blocked order management',
    ],
    previewGradient: 'from-rose-500/20 via-brand-cornflower/20 to-brand-navy/50',
    previewLabel: 'Risk Dashboard',
  },
  {
    id: 'portal',
    icon: <Globe className='h-5 w-5' strokeWidth={1.5} />,
    title: 'Customer Portal',
    bullets: [
      'Self-service invoice browsing and deduction filing',
      'Online payment simulation',
      'Supplier network onboarding',
    ],
    previewGradient: 'from-violet-500/20 via-brand-purple/20 to-brand-navy/50',
    previewLabel: 'Portal View',
  },
]

const AUTO_ROTATE_MS = 6000

export function ProductShowcase({ className }: { className?: string }) {
  const [activeTab, setActiveTab] = useState(0)

  const next = useCallback(() => {
    setActiveTab((prev) => (prev + 1) % PILLARS.length)
  }, [])

  useEffect(() => {
    const interval = setInterval(next, AUTO_ROTATE_MS)
    return () => clearInterval(interval)
  }, [next])

  const active = PILLARS[activeTab]

  return (
    <div
      className={cn(
        'relative flex h-screen flex-col justify-between overflow-hidden bg-brand-navy px-8 py-6 lg:px-12 lg:py-8',
        className
      )}
    >
      {/* Ambient gradient blobs */}
      <div className='pointer-events-none absolute inset-0'>
        <div className='absolute -left-1/4 -top-1/4 h-[600px] w-[600px] rounded-full bg-brand-purple/20 blur-[120px]' />
        <div className='absolute -bottom-1/4 -right-1/4 h-[500px] w-[500px] rounded-full bg-brand-cornflower/15 blur-[100px]' />
      </div>

      {/* Content */}
      <div className='relative z-10 flex flex-1 flex-col justify-between gap-5'>
        {/* Branding */}
        <div>
          <div className='flex items-center gap-3'>
            <Logomark variant='light' size={32} />
            <span className='text-base font-semibold text-white'>Supervity</span>
          </div>
          <h1 className='mt-4 text-display-3 font-bold leading-tight text-white'>
            AR Command Center
          </h1>
          <p className='mt-2 max-w-md text-sm leading-relaxed text-white/60'>
            The AI-first accounts receivable platform that automates cash
            application, collections, deduction resolution, and credit management.
          </p>
        </div>

        {/* Feature tabs */}
        <div className='space-y-3'>
          {/* Tab pills */}
          <div className='flex flex-wrap gap-2'>
            {PILLARS.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActiveTab(i)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-300',
                  i === activeTab
                    ? 'bg-white/15 text-white shadow-sm backdrop-blur-sm'
                    : 'text-white/50 hover:bg-white/5 hover:text-white/70'
                )}
              >
                {p.icon}
                {p.title}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className='min-h-[110px]'>
            <AnimatePresence mode='wait'>
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className='flex gap-6'
              >
                {/* Text content */}
                <div className='flex-1 space-y-2'>
                  <h3 className='text-base font-semibold text-white'>
                    {active.title}
                  </h3>
                  <ul className='space-y-1'>
                    {active.bullets.map((b) => (
                      <li
                        key={b}
                        className='flex items-start gap-2 text-sm text-white/60'
                      >
                        <span className='mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand-cornflower' />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Screenshot preview */}
                <div
                  className={cn(
                    'hidden h-24 w-32 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br xl:flex',
                    active.previewGradient
                  )}
                >
                  <div className='text-center'>
                    <div className='mx-auto mb-1 text-white/60'>
                      {active.icon}
                    </div>
                    <p className='text-[10px] font-medium text-white/40'>
                      {active.previewLabel}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Metrics */}
        <MetricCounters />

        {/* Trusted by */}
        <TrustedByBar />
      </div>
    </div>
  )
}
